// getRemoteConfigData.js
'use strict';
import { Collection, List, Map } from 'immutable';

import replaceTemplate from '../resource/replaceTemplate.js';
import createCachedSelector from '../resource/createCachedSelector.js';
import { castConfigValue } from '../resource/castConfigValue.js';

const defaultMap = new Map();

/**
 * Merger used by getMergedRemoteConfig. Recurses into nested Maps but treats
 * Lists as leaf values so that an override array fully replaces the base
 * array (instead of Immutable's default index-by-index mergeDeep behavior).
 * @param {*} oldValue - previous value at the key.
 * @param {*} newValue - incoming value at the key.
 * @returns {*} The merged value.
 */
const mergeWithListReplace = (oldValue, newValue) => {
  if (List.isList(oldValue) || List.isList(newValue)) {
    return newValue;
  }
  if (Map.isMap(oldValue) && Map.isMap(newValue)) {
    return oldValue.mergeWith(mergeWithListReplace, newValue);
  }
  return newValue;
};

/**
 * Select the merged remote config map for a given configId.
 * Hoisted out of getRemoteConfigData so the mergeDeep runs once per
 * (state, configId) instead of once per (dataKey, configId, dataObject).
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} configId - configuration id.
 * @return {Immutable.Map} The merged remote config map.
 */
const getMergedRemoteConfig = createCachedSelector(
  state => state.get('remoteConfig'),
  state => state.getIn(['config', 'result']),
  (state, configId) =>
    state.getIn(['operations', 'config', 'configurationData', configId]),
  (
    baseRemoteConfig = defaultMap,
    baseConfig = defaultMap,
    configIdRemoteConfig = defaultMap
  ) =>
    baseRemoteConfig
      .mergeWith(mergeWithListReplace, baseConfig)
      .mergeWith(mergeWithListReplace, configIdRemoteConfig)
)((state, configId) => `${configId}`);

/**
 * Select remote config data by data key
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} dataKey - select key.
 * @param {string} configId - configuration id.
 * @param {string} dataObject - data object.
 * @return {*} The selected remote config data.
 */
const getRemoteConfigData = createCachedSelector(
  (state, dataKey, configId) =>
    getMergedRemoteConfig(state, configId)?.get(dataKey),
  (state, dataKey) => dataKey,
  (state, dataKey, configId, dataObject) =>
    dataObject != null ? JSON.stringify(dataObject) : undefined,
  (raw, dataKey, dataJson) => {
    let result = raw instanceof Collection ? raw.toJS() : raw;

    result = castConfigValue({ dataKey, value: result });

    // Replace template string to real data
    const templateDataObject = dataJson ? JSON.parse(dataJson) : undefined;
    if (templateDataObject) {
      if (typeof result === 'string') {
        result = replaceTemplate({
          string: result,
          data: templateDataObject,
        });
      } else if (Array.isArray(result)) {
        result = result
          .filter(i => !(i == null || i === ''))
          .map(item => {
            if (typeof item === 'string') {
              return replaceTemplate({
                string: item,
                data: templateDataObject,
              });
            }
            return item;
          });
      }
    }

    return result instanceof Collection ? result.toJS() : result;
  }
)(
  (state, dataKey, configId, dataObject) =>
    `${dataKey}:${configId}:${
      dataObject != null ? JSON.stringify(dataObject) : dataObject
    }`
);

export default getRemoteConfigData;
export { getMergedRemoteConfig };
